import { isSheetPartType } from "../helpers/isSheetPartType";
import {
  Layout,
  MaterialLayout,
  ExtendedPart,
  PartTypeSchema,
  PricingModeSchema,
  QuoteRequestLine,
} from "../types";
import { adaptComputedCost } from "./adaptComputedCost";
import { OptimizedMaterialCosts } from "./computeOptimizedMaterialCosts";
import { getMaterialWeight } from "./getMaterialWeight";
import { getSheetMetalQuantity } from "./getSheetMetalQuantity";

type ComputeMaterialPriceReturn = {
  partMaterialPrice: number;
  partMaterialCost: number;
  partMaterialMarginPrice: number;
};

type ComputeMaterialPriceLayoutParam = Layout & {
  materialLayout: MaterialLayout | null;
};

const computeVolumetricMaterialPrice = (
  layout: ComputeMaterialPriceLayoutParam,
  margin: number,
): ComputeMaterialPriceReturn => {
  const materialLayout = layout.materialLayout;
  const density = materialLayout?.density ?? 0; // in g/cm³
  const volume = materialLayout?.volume ?? 0; // in mm3
  const volumetricPrice = materialLayout?.volumetricPrice ?? 0; // in cents/kg

  const weightKg = getMaterialWeight(volume, density);
  const computedPrice = volumetricPrice * weightKg;
  const computedMarginPrice = (computedPrice * margin || 0) / 100;

  const {
    cost: partMaterialCost,
    marginPrice: partMaterialMarginPrice,
    price: partMaterialPrice,
  } = adaptComputedCost({
    cost: computedPrice,
    marginPrice: computedMarginPrice,
  });

  return {
    partMaterialPrice,
    partMaterialCost,
    partMaterialMarginPrice,
  };
};

export type ComputeMaterialPriceParams = {
  quoteRequestLine: QuoteRequestLine & {
    part: ExtendedPart;
    layout: ComputeMaterialPriceLayoutParam | null;
  };
  optimizedMaterialCosts: OptimizedMaterialCosts;
};

const computeMaterialPriceFromCost = (cost: number, margin: number): ComputeMaterialPriceReturn => {
  const {
    cost: partMaterialCost,
    marginPrice: partMaterialMarginPrice,
    price: partMaterialPrice,
  } = adaptComputedCost({ cost, marginPrice: (cost * margin || 0) / 100 });

  return { partMaterialPrice, partMaterialCost, partMaterialMarginPrice };
};

type Dimensions = { length?: number | null; width?: number | null } | null | undefined;

const getArea = (dimensions: Dimensions, isSheet: boolean) =>
  (dimensions?.length ?? 0) * (isSheet ? (dimensions?.width ?? 0) : 1);

export type StockInputs = {
  stockPrice: number;
  stockArea: number;
  partArea: number;
  stocksUsed: number;
  quantity: number;
};

export const getStockInputs = ({
  part,
  materialLayout,
  quantity,
}: {
  part: Dimensions;
  materialLayout: MaterialLayout | null;
  quantity: number;
}): StockInputs => {
  const isSheet = isSheetPartType(materialLayout?.type);

  return {
    stockPrice: materialLayout?.price ?? 0,
    stockArea: getArea(materialLayout, isSheet),
    partArea: getArea(part, isSheet),
    stocksUsed:
      materialLayout?.quantity || getSheetMetalQuantity({ partialQuoteRequestLine: { quantity } }),
    quantity,
  };
};

export const computeStockUnitCost = (
  { stockPrice, stockArea, partArea, stocksUsed, quantity }: StockInputs,
  billedScrapShare: number,
) => {
  const partsTotalArea = quantity * partArea;
  const scrapArea = stocksUsed * stockArea - partsTotalArea;
  const hasSizes = stockArea > 0 && partArea > 0;

  return hasSizes
    ? (stockPrice / stockArea) * partArea * (1 + (billedScrapShare * scrapArea) / partsTotalArea)
    : (stockPrice * stocksUsed) / quantity;
};

export const newComputeMaterialPrice = ({
  quoteRequestLine,
  optimizedMaterialCosts,
}: ComputeMaterialPriceParams): ComputeMaterialPriceReturn => {
  const { materialMarginRatio, quantity, layout } = quoteRequestLine;

  const materialMargin = materialMarginRatio ?? 0;

  const optimizedCost = optimizedMaterialCosts.get(quoteRequestLine.id);
  if (optimizedCost !== undefined) {
    return computeMaterialPriceFromCost(optimizedCost, materialMargin);
  }

  if (layout && layout.materialLayout?.pricingMode === PricingModeSchema.enum.VOLUMETRIC) {
    return computeVolumetricMaterialPrice(layout, materialMargin);
  }

  if (!quantity || !layout || layout.useNesting === null) {
    return {
      partMaterialPrice: 0,
      partMaterialCost: 0,
      partMaterialMarginPrice: 0,
    };
  }

  const { materialLayout } = layout;
  const stockPrice = materialLayout?.price ?? 0;
  const layoutType = materialLayout?.type;
  const isSingleUnit =
    !layout.useNesting || layoutType === PartTypeSchema.enum.OTHER || layoutType === null;

  if (isSingleUnit) {
    return computeMaterialPriceFromCost(stockPrice, materialMargin);
  }

  const stockInputs = getStockInputs({ part: quoteRequestLine.part, materialLayout, quantity });
  const billedScrapShare = (layout.newScrapMaterialBillingRate || 0) / 100;

  return computeMaterialPriceFromCost(
    computeStockUnitCost(stockInputs, billedScrapShare),
    materialMargin,
  );
};
